import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { HeartHandshake } from "lucide-react";
import { Switch } from "./ui/switch";
import { api, errMessage } from "../lib/api";

const FREQUENCIES = [
  ["off", "Off"],
  ["weekly", "Weekly"],
  ["biweekly", "Every two weeks"],
];

// Gentle, opt-in encouragement reminders for INVITED family adults.
// Off by default; sandbox email previews only; zero pressure language.
export const EncouragementReminderPanel = () => {
  const [settings, setSettings] = useState(null);
  const [emails, setEmails] = useState([]);

  const load = useCallback(() => {
    api.get("/encouragement/settings").then(({ data }) => setSettings(data)).catch(() => setSettings(null));
    api.get("/encouragement/emails").then(({ data }) => setEmails(data.emails)).catch(() => setEmails([]));
  }, []);
  useEffect(load, [load]);

  if (!settings) return null; // flag off or endpoint unavailable

  const setFrequency = async (frequency) => {
    try {
      await api.put("/encouragement/settings", { frequency });
      setSettings({ ...settings, frequency });
      toast.success(frequency === "off"
        ? "Reminders off — no more nudges"
        : "Lovely — we'll send at most one gentle note " + (frequency === "weekly" ? "a week" : "every two weeks"));
    } catch (e) {
      toast.error(errMessage(e));
    }
  };

  const toggleFamilyDisable = async (disabled) => {
    try {
      await api.put("/encouragement/family-disable", { disabled });
      setSettings({ ...settings, family_disabled: disabled });
      toast.success(disabled ? "Encouragement reminders disabled for your family" : "Family reminders allowed again");
    } catch (e) {
      toast.error(errMessage(e));
    }
  };

  // Owner view: a single family-level control.
  if (settings.i_am_owner) {
    return (
      <div className="bg-card rounded-2xl border border-border p-5 space-y-2" data-testid="encouragement-owner-panel">
        <div className="flex items-center justify-between gap-3 flex-wrap">
          <div className="flex items-center gap-2">
            <HeartHandshake className="h-4 w-4 text-secondary" aria-hidden="true" />
            <p className="text-sm font-bold">Family encouragement reminders</p>
          </div>
          <div className="flex items-center gap-2">
            <span className="text-xs text-muted-foreground">Allow for my family</span>
            <Switch checked={!settings.family_disabled}
              onCheckedChange={(v) => toggleFamilyDisable(!v)}
              aria-label="Allow encouragement reminders for my family"
              data-testid="encouragement-family-toggle" />
          </div>
        </div>
        <p className="text-xs text-muted-foreground">
          Invited family members can choose an occasional, gentle email nudge to send a cheer.
          Turning this off stops all reminders for everyone in your family immediately.
        </p>
      </div>
    );
  }

  // Invited member view.
  if (!settings.eligible && settings.frequency === "off") return null;

  return (
    <div className="bg-card rounded-2xl border border-border p-5 space-y-3" data-testid="encouragement-member-panel">
      <div className="flex items-center gap-2">
        <HeartHandshake className="h-4 w-4 text-secondary" aria-hidden="true" />
        <p className="text-sm font-bold">Gentle encouragement reminders</p>
      </div>
      <p className="text-xs text-muted-foreground">
        If you'd like, we can send you an occasional note — just a soft reminder that you can
        send a little encouragement. No streaks, no pressure, and you can stop anytime.
      </p>
      <div className="flex gap-2 flex-wrap" data-testid="encouragement-frequency-options">
        {FREQUENCIES.map(([key, label]) => (
          <button key={key} type="button" onClick={() => setFrequency(key)}
            className={`px-4 py-1.5 rounded-full text-sm font-semibold border transition-colors
              ${settings.frequency === key
                ? "bg-secondary text-secondary-foreground border-secondary"
                : "bg-transparent border-border text-muted-foreground hover:border-secondary"}`}
            data-testid={`encouragement-frequency-${key}`}>
            {label}
          </button>
        ))}
      </div>
      {settings.family_disabled && (
        <p className="text-xs text-destructive" data-testid="encouragement-family-disabled-note">
          The family owner has paused encouragement reminders for everyone right now.
        </p>
      )}
      {emails.length > 0 && (
        <details className="rounded-xl border border-border" data-testid="encouragement-email-previews">
          <summary className="cursor-pointer px-3 py-2 text-xs font-semibold list-none">
            Reminder previews ({emails.length}) — sandbox only, nothing is delivered
          </summary>
          <div className="px-3 pb-2 space-y-1">
            {emails.map((e) => (
              <p key={e.email_id} className="text-xs text-muted-foreground">
                {new Date(e.created_at).toLocaleString()} · {e.subject}
              </p>
            ))}
          </div>
        </details>
      )}
    </div>
  );
};
